import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useAuthModal } from "../../contexts/AuthModalContext";

const features = [
  {
    icon: (
      <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
    iconBg: "bg-gray-100",
    iconColor: "text-gray-600",
    title: "Smart Itineraries",
    description: "Our advanced AI engine curates bespoke journey plans based on your unique travel DNA. From hidden gems to must-see landmarks, every stop is tailored to you.",
  },
  {
    icon: (
      <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    ),
    iconBg: "bg-orange-100",
    iconColor: "text-orange-600",
    title: "Real-Time Budgeting",
    description: "Maintain total financial control. Our system tracks costs in real-time, suggesting optimizations so you can maximize your adventure without exceeding your limits.",
    highlighted: true,
  },
  {
    icon: (
      <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
      </svg>
    ),
    iconBg: "bg-teal-100",
    iconColor: "text-teal-600",
    title: "Cost-Effective Deals",
    description: "We meta-search across platforms to find exclusive rates on multi-city flights and bundled hotels, automatically securing the best prices with zero effort from you.",
    hasFloatingIcon: true,
  },
];

function FeatureCards({ className = "" }) {
  return (
    <div className={`px-4 sm:px-6 lg:px-8 ${className}`}>
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5 lg:gap-6">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`relative bg-white rounded-xl sm:rounded-2xl p-4 sm:p-5 lg:p-6 shadow-lg hover:shadow-xl transition-shadow ${
                feature.highlighted ? "ring-2 ring-orange-200" : ""
              }`}
            >
              <div className={`w-10 h-10 sm:w-11 sm:h-11 lg:w-12 lg:h-12 ${feature.iconBg} ${feature.iconColor} rounded-lg sm:rounded-xl flex items-center justify-center mb-3 sm:mb-4`}>
                {feature.icon}
              </div>

              <h3 className="text-sm sm:text-base lg:text-lg font-bold text-gray-900 mb-2 sm:mb-3">
                {feature.title}
              </h3>

              <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
                {feature.description}
              </p>

              {feature.hasFloatingIcon && (
                <div className="absolute -bottom-2 -right-2 sm:-bottom-3 sm:-right-3 w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-teal-500 rounded-lg sm:rounded-xl flex items-center justify-center text-white shadow-lg">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function HeroSection() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { open: openAuthModal } = useAuthModal();

  const handleGenerateItinerary = () => {
    if (isAuthenticated) {
      navigate("/plan");
    } else {
      openAuthModal({ returnTo: "/plan" });
    }
  };

  return (
    <>
      <section className="relative min-h-screen md:h-[92vh] md:min-h-[700px] flex flex-col">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(to bottom, rgba(15, 50, 50, 0.85), rgba(20, 60, 60, 0.9)), url('https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&q=80')`,
          }}
        />

        <div className="relative z-10 flex-1 flex flex-col items-center justify-center text-center px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto w-full py-24 md:py-0">
          <div className="inline-flex items-center gap-2 bg-teal-700/50 backdrop-blur-sm text-white text-[10px] sm:text-xs font-semibold tracking-wider uppercase px-3 sm:px-4 py-1.5 sm:py-2 rounded-full mb-5 sm:mb-6 md:mb-8 mx-auto">
            <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
            Intelligent Trip Generation
          </div>

          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-1 sm:mb-2">
            <span className="italic">Your Perfect Trip,</span>
          </h1>
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 sm:mb-5 md:mb-6">
            <span className="italic bg-gradient-to-r from-teal-400 via-emerald-400 to-orange-400 bg-clip-text text-transparent">
              Generated in Seconds.
            </span>
          </h1>

          <p className="text-gray-300 text-xs sm:text-sm md:text-base lg:text-lg max-w-xl md:max-w-2xl mx-auto mb-6 sm:mb-8 md:mb-10 leading-relaxed px-2">
            Experience the next evolution of travel. The Romaya AI Planner builds complete, personalized itineraries based on your budget and passions—no chatting required.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <button
              onClick={handleGenerateItinerary}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold px-5 sm:px-6 py-2.5 sm:py-3 rounded-full transition-colors shadow-lg text-sm sm:text-base cursor-pointer"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
              </svg>
              Generate My Itinerary
            </button>
            <a
              href="#how-it-works"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gray-800/60 hover:bg-gray-800/80 backdrop-blur-sm text-white font-semibold px-5 sm:px-6 py-2.5 sm:py-3 rounded-full transition-colors border border-gray-600 text-sm sm:text-base"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              See How It Works
            </a>
          </div>
        </div>

        <FeatureCards className="hidden md:block absolute bottom-0 left-0 right-0 translate-y-1/2 z-20" />
      </section>

      <div className="md:hidden bg-gray-100 pt-8 pb-12 px-4">
        <FeatureCards />
      </div>
    </>
  );
}
